import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import styled, { css } from "styled-components";

import LandingBackground2 from "assets/png/landingPage/LandingBackground2.png";
import LandingHeroImage from "assets/png/landingPage/LandingHeroImage.png";
import StarIcon from "assets/png/landingPage/StarIcon.png";

import HtmlHead from "components/atoms/HtmlHead";
import Image from "components/atoms/Image";
import Text from "components/atoms/text/Text";

import FlashingStar from "components/organisms/landing/FlashingStar";
import ServiceIntroSection from "components/organisms/landing/ServiceIntroSection";
import GuestUserLayout from "components/templates/GuestUserLayout";

import { useDevice } from "utils/device";

import AOS from "aos";
import "aos/dist/aos.css";

const ContentsWrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 0;
    padding: 0;
    width: 100%;
    min-height: calc(100vh - 48px);

    ${(props) =>
        props.$device.isTabletOrPC &&
        css`
            min-height: calc(100vh - 56px);
        `};
`;

const TopSection = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 64px 0 0 0;
    width: 100%;
    height: auto;

    ${(props) =>
        props.$device.isTabletOrPC &&
        css`
            padding: 24px 0 0 0;
        `};
`;

const WidthLimit = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin: 0 auto;
    padding: 0 40px;
    width: 100%;
    max-width: 1080px;

    ${(props) =>
        props.$device.isTabletOrPC &&
        css`
            flex-direction: row;
            justify-content: space-between;
        `};
`;

const LogoCopyWrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 0;
    width: 100%;

    ${(props) =>
        props.$device.isTabletOrPC &&
        css`
            align-items: flex-start;
            margin: 0 20px 0 0;
            width: calc(100% - 340px);
        `};

    ${(props) =>
        props.$device.isPC &&
        css`
            width: calc(100% - 460px);
        `};
`;

const LandingLogoWrap = styled.div`
    height: 32px;
    transform: scale(0.8);

    > img {
        height: 100%;
    }

    ${(props) =>
        props.$device.isTabletOrPC &&
        css`
            height: 40px;
            transform: scale(1);
        `};
`;

const HeroMessage = styled.h1`
    margin: 24px 0 0 0;
    font-size: 32px;
    font-family: "LINESeedKR";
    color: var(--color-NormalBlack);
    text-align: center;

    ${(props) =>
        props.$device.isTabletOrPC &&
        css`
            font-size: 40px;
            text-align: left;
            margin: 40px 0 0 0;
        `};
`;

const HeroSubMessage = styled(Text).attrs((props) => ({
    variant: "body-lg-400",
    margin: props.$device?.isTabletOrPC ? "24px 0 0 0" : "16px 0 0 0",
    textAlign: props.$device?.isTabletOrPC ? "left" : "center",
    width: "fit-content",
    height: "fit-content",
    color: "var(--color-SubBlack)",
}))``;

const KeyColorText = styled.span`
    color: var(--color-Key);
`;

const LandingImageWrap = styled.div`
    position: relative;
    margin: 24px 0 0 0;
    padding: 24px 0;
    width: 240px;

    > img {
        width: 100%;
        opacity: 0.9;
    }

    ${(props) =>
        props.$device.isTabletOrPC &&
        css`
            display: block;
            margin: 0;
            width: 360px;
        `};

    ${(props) =>
        props.$device.isPC &&
        css`
            width: 440px;
        `};
`;

const AnimationWrap = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: transparent;
`;

const LastSection = styled.div`
    position: relative;
    width: 100%;
    margin: 0 0 24px 0;
    padding: 80px 0;

    ${(props) =>
        props.$device.isTabletOrPC &&
        css`
            margin: 0 0 80px 0;
        `};
`;

const LastSectionContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 0 auto;
    padding: 0 24px;
    width: 100%;
    max-width: 920px;

    ${(props) =>
        props.$device.isTabletOrPC &&
        css`
            padding: 0 40px;
        `};

    ${(props) =>
        props.$device.isPC &&
        css`
            padding: 0 80px;
        `};
`;

const CopyText = styled.p`
    margin: 0 0 16px 0;
    font-size: 18px;
    font-family: "LineSeedKR";
    font-weight: 400;
    text-align: center;
    color: var(--color-Black);

    ${(props) =>
        props.$device.isTabletOrPC &&
        css`
            font-size: 20px;
        `};

    ${(props) =>
        props.$device.isPC &&
        css`
            font-size: 24px;
        `};
`;

const EmphasisText = styled.span`
    margin: 0 0 0 8px;
    font-weight: 700;
    color: var(--color-DarkKey);
`;

const StartText = styled.h2`
    margin: 0 0 24px 0;
    font-size: 32px;
    font-family: "LineSeedKR";
    font-weight: 700;
    color: var(--color-Black);

    ${(props) =>
        props.$device.isTabletOrPC &&
        css`
            margin: 0 0 48px 0;
            font-size: 40px;
        `};

    ${(props) =>
        props.$device.isPC &&
        css`
            font-size: 48px;
        `};
`;

const StartButton = styled.button`
    position: relative;
    display: flex;
    align-items: center;
    padding: 12px 28px;
    border: transparent;
    border-radius: 12px;
    background: linear-gradient(180deg, #00725d, #066786);
    cursor: pointer;
    outline: none;
    transition: all 120ms ease-in;

    &:after {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 100%;
        opacity: 0;
        content: "";
        display: block;
        width: 100%;
        height: 100%;
        border-radius: 12px;
        background-color: rgba(0, 0, 0, 0.6);
        transition: all 200ms ease-in;
    }

    &:hover {
        &:after {
            opacity: 1;
        }
    }

    ${(props) =>
        props.$device.isPC &&
        css`
            padding: 16px 40px;
        `};
`;

const StartIconWrap = styled.div`
    margin: 0 8px 2px 0;
    width: 18px;
    height: 18px;
    z-index: 1;

    img {
        width: 100%;
    }

    ${(props) =>
        props.$device.isPC &&
        css`
            margin: 0 12px 4px 0;
            width: 24px;
            height: 24px;
        `};
`;

const StartButtonText = styled.p`
    margin: 1px 0 0 0;
    font-size: 18px;
    font-family: "LineSeedKR";
    font-weight: 700;
    color: var(--color-White);
    z-index: 1;

    ${(props) =>
        props.$device.isTabletOrPC &&
        css`
            font-size: 20px;
        `};

    ${(props) =>
        props.$device.isPC &&
        css`
            font-size: 24px;
        `};
`;

const LastSecBgImage = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    margin: 0 auto;
    width: calc(100% - 48px);
    height: 100%;
    border-radius: 16px;
    text-align: center;
    overflow: hidden;
    z-index: -1;

    img {
        width: auto;
        height: 100%;
    }

    ${(props) =>
        props.$device.isTabletOrPC &&
        css`
            width: calc(100% - 80px);
        `};

    ${(props) =>
        props.$device.isPC &&
        css`
            width: calc(100% - 160px);

            img {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
        `};
`;

function LandingPage(props) {
    const device = useDevice();

    const navigate = useNavigate();

    useEffect(() => {
        AOS.init({
            offset: 100,
            duration: 800,
            easing: "ease-in-out",
            once: true,
        });
    }, []);

    return (
        <GuestUserLayout>
            <HtmlHead title={"PREP"} />
            <ContentsWrap $device={device}>
                <TopSection $device={device}>
                    <WidthLimit $device={device}>
                        <LogoCopyWrap data-aos="fade-up" $device={device}>
                            <LandingLogoWrap $device={device}>
                                <Image name={"landingLogo"} />
                            </LandingLogoWrap>
                            <HeroMessage $device={device}>
                                연구자를 위한 {!device.isTabletOrPC && <br />}최고의 선택{" "}
                                <KeyColorText>프렙</KeyColorText>
                            </HeroMessage>

                            <HeroSubMessage $device={device}>
                                논문 검색과 참고문헌 관리부터
                                <br />
                                AI와 함께하는 PDF 리뷰 및 동향 분석 연구 노트까지,
                                <br />
                                개인, 그룹 단위의 연구 공간에서 당신의 연구 속도를 높여보세요.
                            </HeroSubMessage>
                        </LogoCopyWrap>
                        <LandingImageWrap data-aos="fade-up" data-aos-delay="50" $device={device}>
                            <img src={LandingHeroImage} alt={"배경이미지"} />
                            <AnimationWrap>
                                <FlashingStar />
                            </AnimationWrap>
                        </LandingImageWrap>
                    </WidthLimit>
                </TopSection>

                <ServiceIntroSection />

                {/* 가입 권유 section */}
                <LastSection $device={device}>
                    <LastSectionContent $device={device}>
                        <CopyText className="lastSection" $device={device} data-aos="fade-up">
                            <span>연구자를 위한 최고의 선택</span>
                            <EmphasisText>프렙!</EmphasisText>
                        </CopyText>
                        <StartText
                            $device={device}
                            data-aos-anchor=".lastSection"
                            data-aos="fade-up"
                            data-aos-delay="50"
                        >
                            지금 시작해보세요
                        </StartText>
                        <StartButton
                            $device={device}
                            data-aos-anchor=".lastSection"
                            data-aos="fade-in"
                            data-aos-delay="400"
                            onClick={(e) => {
                                navigate("/signup");
                            }}
                        >
                            <StartIconWrap $device={device}>
                                <img src={StarIcon} alt={"별아이콘"} />
                            </StartIconWrap>
                            <StartButtonText $device={device}>회원가입하기</StartButtonText>
                        </StartButton>
                    </LastSectionContent>
                    <LastSecBgImage $device={device}>
                        <img src={LandingBackground2} alt={"백그라운드이미지"} />
                    </LastSecBgImage>
                </LastSection>
            </ContentsWrap>
        </GuestUserLayout>
    );
}

export default LandingPage;
